import { useMemo, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  TextField as MuiTextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import type { ProcessedFormField } from "@/types/api/formSchema";

type Props = {
  field: ProcessedFormField;
  value: string[] | string | null | undefined;
  onChange: (value: string[]) => void;
  onBlur: () => void;
  name: string;
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function toArray(value: string[] | string | null | undefined): string[] {
  if (Array.isArray(value)) return value as string[];
  if (typeof value === "string" && value)
    return value
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  return [];
}

export function EmailsField({
  field: schemaField,
  value,
  onChange,
  onBlur,
  name,
}: Props) {
  const arr = useMemo(() => toArray(value), [value]);
  const rows = arr.length > 0 ? arr : [""];
  const [errors, setErrors] = useState<Record<number, string | undefined>>({});

  const setAt = (idx: number, next: string) => {
    const updated = rows.slice();
    updated[idx] = next;
    onChange(updated);
    setErrors((prev) => ({ ...prev, [idx]: undefined }));
  };

  const removeAt = (idx: number) => {
    const updated = rows.filter((_, i) => i !== idx);
    onChange(updated);
    setErrors((prev) => {
      // Shift down error keys > idx so they stay aligned with the surviving rows.
      const next: Record<number, string | undefined> = {};
      Object.entries(prev).forEach(([k, v]) => {
        const i = Number(k);
        if (i < idx) next[i] = v;
        else if (i > idx) next[i - 1] = v;
      });
      return next;
    });
  };

  const addRow = () => {
    onChange([...rows, ""]);
  };
  const canAdd = rows.every((r) => EMAIL_PATTERN.test(r.trim()));

  const normalizeOnBlur = () => {
    const seen = new Set<string>();
    const newErrors: Record<number, string | undefined> = {};
    const trimmed = rows.map((r) => r.trim());
    trimmed.forEach((val, i) => {
      if (!val) return;
      if (!EMAIL_PATTERN.test(val)) {
        newErrors[i] = "Invalid email address";
        return;
      }
      const key = val.toLowerCase();
      if (seen.has(key)) {
        newErrors[i] = "Duplicate email";
        return;
      }
      seen.add(key);
    });
    setErrors(newErrors);
    // Drop empty rows on blur, but KEEP invalid/duplicate rows so the vendor
    // can see and fix their own typed input. The previous impl also filtered
    // invalids out, which left the inline error under an empty input.
    const cleaned = trimmed.filter((v) => v !== "");
    if (
      cleaned.length !== rows.length ||
      cleaned.some((v, i) => v !== rows[i])
    ) {
      onChange(cleaned);
    }
  };

  const disabled = !!schemaField.disabled;

  return (
    <Box>
      {rows.map((val, idx) => {
        const rowId = `${name}-${idx}`;
        return (
        <Box
          key={idx}
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
            mb: 1,
          }}
        >
          <MuiTextField
            id={rowId}
            name={rowId}
            value={val}
            onChange={(e) => setAt(idx, e.target.value)}
            onBlur={() => {
              normalizeOnBlur();
              onBlur();
            }}
            error={!!errors[idx]}
            helperText={errors[idx]}
            disabled={disabled}
            placeholder={
              schemaField.placeholder ?? "name@example.com"
            }
            fullWidth
            size="small"
            variant="outlined"
            inputProps={{ autoComplete: rowId }}
          />
          {!disabled && (
            <IconButton
              type="button"
              size="small"
              onClick={() => removeAt(idx)}
              aria-label="Remove email"
              sx={{ mt: 0.5, color: "text.secondary" }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
        );
      })}
      {!disabled && (
        <Button
          type="button"
          size="small"
          startIcon={<AddIcon />}
          disabled={!canAdd}
          onClick={addRow}
          sx={{
            textTransform: "none",
            color: "primary.main",
            fontWeight: 600,
            px: 0,
            "&:hover": { bgcolor: "transparent" },
          }}
        >
          Add another
        </Button>
      )}
    </Box>
  );
}
